import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TableSkeleton } from '../Skeleton';
import { UserPickerRow } from '../UserPickerRow';
import { ConfirmDialog } from '../ConfirmDialog';
import { User } from '../../types';
import { useAffiliateUserSearch } from '../../hooks/useAffiliateUserSearch';
import {
  adminService,
  HelpdeskOperatorRow,
  RemoteSupportAdminState,
  RemoteSupportSessionList,
} from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';
import { endReasonLabel, endedByLabel, filesLabel, formatDuration, formatSessionTime } from '../../utils/remoteSupportLabels';
import { AlertTriangle, MousePointer2, Paperclip, Plus, RefreshCw, ScreenShare, Search, UserMinus, X } from 'lucide-react';
import { ADMIN_LIST_CAPPED } from './adminLayout';

/**
 * 원격지원 관리 (관리자 콘솔 '원격지원' 탭, super_admin 전용)
 *
 * - 정책: 원격지원 전체 / 원격 제어(마우스·키보드) 두 스위치. 기본 꺼짐
 * - 담당자 = 이 명단 + super_admin(자동, 2026-10-08~). 담당자는 직원의 요청을 받고(직원이 담당자 프로필에서
 *   '원격지원 요청'), 직원 프로필에서 원격지원을 시작할 수도 있다. super_admin은 '자동' 행으로 보이고 해제 버튼이 없다
 * - 세션 이력: 기간·담당자·직원으로 거른다
 *
 * 사번을 직접 입력하지 않고 명부에서 고른다(LoginApprovalManager와 같은 이유 — 오타가 그대로 저장된다).
 */

const PERIODS = [
  { days: 7, label: '최근 7일' },
  { days: 30, label: '최근 30일' },
  { days: 90, label: '최근 90일' },
  { days: 365, label: '최근 1년' },
];

interface RemoteSupportManagerProps {
  currentUser: User;
}

export const RemoteSupportManager: React.FC<RemoteSupportManagerProps> = ({ currentUser }) => {
  const { showToast } = useToast();

  const [state, setState] = useState<RemoteSupportAdminState | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [policyBusy, setPolicyBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirm, setConfirm] = useState<
    | { kind: 'revoke'; row: HelpdeskOperatorRow }
    | { kind: 'disable' }
    | { kind: 'disable-control' }
    | { kind: 'disable-files' }
    | null
  >(null);

  // 세션 이력
  const [days, setDays] = useState(30);
  const [operatorFilter, setOperatorFilter] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [history, setHistory] = useState<RemoteSupportSessionList | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const search = useAffiliateUserSearch({
    isActive: true,
    defaultAffiliateId: currentUser.affiliateId,
  });
  const affiliateLabelOf = useCallback(
    (id: string | null) => (id ? search.affiliates.find(a => a.id === id)?.label ?? id : ''),
    [search.affiliates]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setState(await adminService.getRemoteSupport());
    } catch (err) {
      console.error('Failed to load remote support settings:', err);
      setLoadError(err instanceof Error ? err.message : '원격지원 설정을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setHistory(await adminService.getRemoteSupportSessions({
        days,
        operatorId: operatorFilter || undefined,
        q: query || undefined,
      }));
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '원격지원 기록을 불러오지 못했습니다.' });
    } finally {
      setHistoryLoading(false);
    }
  }, [days, operatorFilter, query, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // 검색어는 입력이 멈춘 뒤에 서버로 보낸다
  useEffect(() => {
    const t = setTimeout(() => setQuery(queryInput.trim()), 300);
    return () => clearTimeout(t);
  }, [queryInput]);

  const operatorKeys = useMemo(
    () => new Set((state?.operators ?? []).map(o => `${o.affiliateId}:${o.userId}`)),
    [state]
  );
  // super_admin(자동 담당자). 명단에도 있는 사람은 명단 행에 배지로만 보인다(두 줄로 겹치지 않게)
  const superAdminIds = useMemo(() => new Set((state?.superAdmins ?? []).map(sa => sa.userId)), [state]);
  const superAdminsOnly = useMemo(
    () => (state?.superAdmins ?? []).filter(sa => !(state?.operators ?? []).some(o => o.userId === sa.userId)),
    [state]
  );
  const isOperator = useCallback(
    (userId: string | number) => operatorKeys.has(`${search.selectedAffiliateId}:${String(userId)}`),
    [operatorKeys, search.selectedAffiliateId]
  );
  const selectedNew = useMemo(
    () => search.selectedUsers.filter(u => !isOperator(u.id)),
    [search.selectedUsers, isOperator]
  );

  const applyPolicy = async (input: { enabled?: boolean; controlEnabled?: boolean; fileTransferEnabled?: boolean }) => {
    if (policyBusy) return;
    setPolicyBusy(true);
    try {
      const policy = await adminService.updateRemoteSupportPolicy(input);
      setState(prev => (prev ? { ...prev, policy } : prev));
      if (input.enabled !== undefined) {
        showToast({
          type: 'success',
          message: input.enabled ? '원격지원을 켰습니다.' : '원격지원을 껐습니다. 진행 중이던 원격지원도 끝났습니다.',
        });
      } else if (input.fileTransferEnabled !== undefined) {
        showToast({
          type: 'success',
          message: input.fileTransferEnabled
            ? '파일 주고받기를 허용했습니다.'
            : '파일 주고받기를 막았습니다. 주고받던 파일도 멈췄습니다.',
        });
      } else {
        showToast({
          type: 'success',
          message: input.controlEnabled ? '원격 제어를 허용했습니다.' : '원격 제어를 막았습니다. 화면 보기는 계속 쓸 수 있습니다.',
        });
      }
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '원격지원 설정을 저장하지 못했습니다.' });
    } finally {
      setPolicyBusy(false);
    }
  };

  const handleGrant = async () => {
    if (!search.selectedAffiliateId || selectedNew.length === 0 || busy) return;
    setBusy(true);
    try {
      const res = await adminService.grantHelpdeskOperators({
        affiliateId: search.selectedAffiliateId,
        users: selectedNew.map(u => ({ userId: String(u.id), name: u.username, depart: u.depart })),
      });
      search.reset();
      setPickerOpen(false);
      await load();
      showToast({ type: 'success', message: `${res.added}명을 담당자로 지정했습니다.` });
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '담당자로 지정하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setBusy(false);
    }
  };

  const performRevoke = async (row: HelpdeskOperatorRow) => {
    setBusy(true);
    try {
      await adminService.revokeHelpdeskOperator(row.affiliateId, row.userId);
      await load();
      showToast({ type: 'success', message: `${row.userName || row.userId}님을 담당자에서 해제했습니다.` });
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '담당자에서 해제하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setBusy(false);
    }
  };

  const policy = state?.policy;
  const enabled = policy?.enabled === true;
  const controlEnabled = policy?.controlEnabled === true;
  // 구버전 서버는 필드가 없다 — 켜짐으로 본다(서버가 rs:file:start에서 최종 판정)
  const fileTransferEnabled = enabled && policy?.fileTransferEnabled !== false;

  return (
    <div className="space-y-6">
      {/* 정책 */}
      <section className="p-4 bg-white border border-slate-200 rounded-xl space-y-4">
        <PolicyRow
          icon={ScreenShare}
          title="원격지원"
          on={enabled}
          description={
            enabled
              ? '직원이 담당자에게 원격지원을 요청하거나 담당자가 직원 PC 화면을 보러 갈 수 있습니다. 직원이 매번 PC의 확인창에서 직접 [공유]를 눌러야 모든 모니터 화면이 보입니다.'
              : '원격지원을 요청하거나 시작할 수 없습니다.'
          }
          canToggle={!policy?.envKillSwitch}
          busy={policyBusy || !policy}
          onToggle={() => (enabled ? setConfirm({ kind: 'disable' }) : applyPolicy({ enabled: true }))}
        />
        <div className="border-t border-slate-100" />
        <PolicyRow
          icon={MousePointer2}
          title="원격 제어 (마우스·키보드)"
          on={controlEnabled}
          description={
            !enabled
              ? '원격지원이 꺼져 있어 쓸 수 없습니다.'
              : controlEnabled
                ? '화면을 보는 중에 담당자가 제어를 요청할 수 있습니다. 직원이 따로 한 번 더 허용해야 합니다.'
                : '화면 보기만 할 수 있습니다.'
          }
          canToggle={enabled && !policy?.envControlKillSwitch}
          busy={policyBusy || !policy}
          // 저장값 기준으로 토글한다(전체가 꺼져 있으면 실제 적용값은 항상 false라 버튼이 엇갈린다)
          onToggle={() =>
            policy?.configured.controlEnabled
              ? setConfirm({ kind: 'disable-control' })
              : applyPolicy({ controlEnabled: true })
          }
        />
        <div className="border-t border-slate-100" />
        <PolicyRow
          icon={Paperclip}
          title="파일 주고받기"
          on={fileTransferEnabled}
          description={
            !enabled
              ? '원격지원이 꺼져 있어 쓸 수 없습니다.'
              : fileTransferEnabled
                ? '화면을 보는 중에 담당자가 직원 PC로 파일을 보내거나 직원 PC의 파일을 가져올 수 있습니다. 파일마다 직원이 직접 허용하고, 받은 파일은 다운로드 폴더에 저장만 됩니다(첨부 차단 확장자는 적용하지 않습니다).'
                : '원격지원 중에 파일을 주고받을 수 없습니다.'
          }
          canToggle={enabled && !policy?.envFileKillSwitch}
          busy={policyBusy || !policy}
          onToggle={() =>
            policy?.configured.fileTransferEnabled !== false
              ? setConfirm({ kind: 'disable-files' })
              : applyPolicy({ fileTransferEnabled: true })
          }
        />
        {(policy?.envKillSwitch || policy?.envControlKillSwitch || policy?.envFileKillSwitch) && (
          <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              {policy?.envKillSwitch
                ? '서버 설정(REMOTE_SUPPORT_ENABLED=false)으로 원격지원이 꺼져 있습니다. 이 화면에서는 켤 수 없습니다.'
                : [
                    policy?.envControlKillSwitch &&
                      '서버 설정(REMOTE_SUPPORT_CONTROL_ENABLED=false)으로 원격 제어가 막혀 있습니다.',
                    policy?.envFileKillSwitch &&
                      '서버 설정(REMOTE_SUPPORT_FILE_TRANSFER_ENABLED=false)으로 파일 주고받기가 막혀 있습니다.',
                  ]
                    .filter(Boolean)
                    .join(' ') + ' 이 화면에서는 허용할 수 없습니다.'}
            </span>
          </div>
        )}
        {policy?.updatedAt && (
          <p className="text-xs text-slate-400">
            마지막 변경: {policy.updatedByName} · {new Date(policy.updatedAt).toLocaleString('ko-KR')}
          </p>
        )}
      </section>

      {loadError && (
        <div className="px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded border border-brand-200 flex items-center justify-between">
          <span>{loadError}</span>
          <button onClick={load} className="text-brand-700 underline text-xs ml-2 shrink-0">
            다시 시도
          </button>
        </div>
      )}

      {/* 담당자 명단 */}
      <section>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div>
            <h3 className="text-sm font-medium text-slate-800">원격지원 담당자</h3>
            <p className="text-xs text-slate-500">
              Super Admin은 모두 자동으로 담당자입니다. 그 밖에 원격지원을 할 사람을 추가하세요. 담당자는 직원의 원격지원 요청을 받고, 직원 프로필에서 원격지원을 시작할 수 있습니다.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setPickerOpen(v => !v)}
            aria-expanded={pickerOpen}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            {pickerOpen ? <X size={14} aria-hidden="true" /> : <Plus size={14} aria-hidden="true" />}
            {pickerOpen ? '닫기' : '담당자 추가'}
          </button>
        </div>

        {pickerOpen && (
          <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {search.showAffiliateSelector && (
                <select
                  value={search.selectedAffiliateId}
                  onChange={e => search.setSelectedAffiliateId(e.target.value)}
                  aria-label="계열사 선택"
                  className="px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
                >
                  {search.affiliates.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.label}
                    </option>
                  ))}
                </select>
              )}
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={search.searchQuery}
                  onChange={e => search.setSearchQuery(e.target.value)}
                  aria-label="직원 검색"
                  placeholder="이름이나 부서로 찾기"
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
              <button
                type="button"
                onClick={handleGrant}
                disabled={busy || selectedNew.length === 0}
                className="ml-auto px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                지정 {selectedNew.length > 0 && `(${selectedNew.length})`}
              </button>
            </div>
            {search.loading && search.allUsers.length === 0 ? (
              <TableSkeleton rows={4} cols={2} />
            ) : search.filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">조건에 맞는 직원이 없습니다</div>
            ) : (
              <div className={`${ADMIN_LIST_CAPPED} bg-white border border-slate-200 rounded-lg divide-y divide-slate-100`}>
                {search.filteredUsers.map(user => (
                  <UserPickerRow
                    key={String(user.id)}
                    user={user}
                    onlineUsers={[]}
                    showPresence={false}
                    onToggle={search.toggleUser}
                    badge={
                      isOperator(user.id) ? (
                        <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-brand-100 text-brand-700">담당자</span>
                      ) : undefined
                    }
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {loading && !state ? (
          <TableSkeleton rows={3} cols={3} />
        ) : (state?.operators.length ?? 0) + superAdminsOnly.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-slate-200 rounded-xl">
            <p className="text-sm text-slate-500">아직 원격지원 담당자가 없습니다</p>
            <p className="mt-1 text-xs text-slate-400">담당자를 지정하면 직원이 그 사람의 프로필에서 원격지원을 요청할 수 있습니다</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* super_admin은 명단 없이 자동 — 해제하려면 역할 관리에서 Super Admin을 회수한다 */}
            {superAdminsOnly.map(sa => (
              <div
                key={`sa:${sa.userId}`}
                className="flex items-center justify-between gap-3 p-3 bg-white border border-slate-200 rounded-xl"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{sa.userName || sa.userId}</div>
                  <div className="text-xs text-slate-400 truncate">사번 {sa.userId} · Super Admin이라 자동으로 담당자입니다</div>
                </div>
                <span className="shrink-0 px-2 py-0.5 text-xs rounded-full font-medium bg-slate-100 text-slate-600">자동</span>
              </div>
            ))}
            {state!.operators.map(row => (
              <div
                key={`${row.affiliateId}:${row.userId}`}
                className="flex items-center justify-between gap-3 p-3 bg-white border border-slate-200 rounded-xl"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {row.userName || row.userId}
                    {row.depart && <span className="ml-2 text-xs font-normal text-slate-500">{row.depart}</span>}
                    {superAdminIds.has(row.userId) && (
                      <span className="ml-2 px-1.5 py-0.5 text-[11px] rounded-full font-medium bg-slate-100 text-slate-600">Super Admin</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 truncate">
                    {affiliateLabelOf(row.affiliateId)} · 사번 {row.userId}
                    {row.grantedByName && <> · {row.grantedByName} 지정</>}
                    {' · '}
                    {new Date(row.createdAt).toLocaleDateString('ko-KR')}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirm({ kind: 'revoke', row })}
                  disabled={busy}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-brand-600 text-brand-600 rounded-lg hover:bg-brand-50 transition-colors disabled:opacity-50"
                >
                  <UserMinus size={14} aria-hidden="true" />
                  해제
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 세션 이력 */}
      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h3 className="text-sm font-medium text-slate-800">원격지원 기록</h3>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              aria-label="기간"
              className="px-2 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              {PERIODS.map(p => (
                <option key={p.days} value={p.days}>
                  {p.label}
                </option>
              ))}
            </select>
            <select
              value={operatorFilter}
              onChange={e => setOperatorFilter(e.target.value)}
              aria-label="담당자"
              className="px-2 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              <option value="">전체 담당자</option>
              {superAdminsOnly.map(sa => (
                <option key={`sa:${sa.userId}`} value={sa.userId}>
                  {sa.userName || sa.userId}
                </option>
              ))}
              {(state?.operators ?? []).map(o => (
                <option key={`${o.affiliateId}:${o.userId}`} value={o.userId}>
                  {o.userName || o.userId}
                </option>
              ))}
            </select>
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                aria-hidden="true"
              />
              <input
                type="search"
                value={queryInput}
                onChange={e => setQueryInput(e.target.value)}
                aria-label="직원·세션 번호 검색"
                placeholder="직원 이름이나 세션 번호로 찾기"
                className="w-56 pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onClick={loadHistory}
              disabled={historyLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={historyLoading ? 'animate-spin' : ''} aria-hidden="true" />
              새로고침
            </button>
          </div>
        </div>

        {history && (
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            <SummaryChip label="지원" value={`${history.summary.count}건`} />
            <SummaryChip label="평균 시간" value={formatDuration(history.summary.avgDurationSeconds)} />
            <SummaryChip label="제어 포함" value={`${history.summary.withControl}건`} />
            <SummaryChip label="제어 합계" value={formatDuration(history.summary.totalControlSeconds)} />
          </div>
        )}

        {historyLoading && !history ? (
          <TableSkeleton rows={4} cols={6} />
        ) : !history || history.sessions.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400">이 기간에 원격지원 기록이 없습니다</div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">시작</th>
                  <th className="px-3 py-2 text-left font-medium">담당자</th>
                  <th className="px-3 py-2 text-left font-medium">직원</th>
                  <th className="px-3 py-2 text-right font-medium">총 시간</th>
                  <th className="px-3 py-2 text-right font-medium">제어</th>
                  <th className="px-3 py-2 text-left font-medium">종료</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.sessions.map(s => {
                  const open = expanded === s.id;
                  return (
                    <React.Fragment key={s.id}>
                      <tr
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => setExpanded(open ? null : s.id)}
                        aria-expanded={open}
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">{formatSessionTime(s.acceptedAt || s.requestedAt)}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-800">{s.operatorName}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-800">
                          {s.hostName}
                          <span className="ml-1 text-xs text-slate-400">{affiliateLabelOf(s.hostAffiliateId)}</span>
                          {s.initiatedBy === 'host' && (
                            <span className="ml-1.5 px-1.5 py-0.5 text-[11px] rounded-full font-medium bg-slate-100 text-slate-600">직원 요청</span>
                          )}
                          {(s.filesToHost || s.filesToOperator) ? (
                            <span
                              className="ml-1.5 inline-flex items-center gap-0.5 text-[11px] text-slate-500"
                              title={filesLabel(s.filesToHost, s.filesToOperator, 'admin')}
                            >
                              <Paperclip size={11} aria-hidden="true" />
                              {(s.filesToHost || 0) + (s.filesToOperator || 0)}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-slate-600">{formatDuration(s.durationSeconds)}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-slate-600">
                          {s.controlSeconds > 0 ? formatDuration(s.controlSeconds) : '-'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                          {endReasonLabel(s.endReason)}
                          {s.endedBy && <span className="ml-1 text-xs text-slate-400">({endedByLabel(s.endedBy)})</span>}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-slate-50">
                          <td colSpan={6} className="px-3 py-3 text-xs text-slate-600">
                            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                              <dt className="text-slate-400">세션 번호</dt>
                              <dd>{s.sessionCode}</dd>
                              <dt className="text-slate-400">담당자 사번</dt>
                              <dd>{s.operatorId}</dd>
                              <dt className="text-slate-400">직원 사번</dt>
                              <dd>{s.hostId}</dd>
                              <dt className="text-slate-400">PC</dt>
                              <dd>
                                {s.hostIp || '-'}
                                {s.hostDeviceId && <span className="ml-2 text-slate-400">기기 {s.hostDeviceId.slice(0, 8)}</span>}
                              </dd>
                              <dt className="text-slate-400">공유한 화면</dt>
                              <dd>{s.monitorLabel || '-'}</dd>
                              <dt className="text-slate-400">입력</dt>
                              <dd>{s.inputEvents.toLocaleString('ko-KR')}건</dd>
                              <dt className="text-slate-400">파일</dt>
                              <dd>
                                {filesLabel(s.filesToHost, s.filesToOperator, 'admin') || '-'}
                                {(s.filesToHost || s.filesToOperator) ? (
                                  <span className="ml-2 text-slate-400">이름·크기는 감사 로그에 있습니다</span>
                                ) : null}
                              </dd>
                              <dt className="text-slate-400">처리 메모</dt>
                              <dd className="whitespace-pre-wrap break-anywhere">{s.note || '-'}</dd>
                            </dl>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ConfirmDialog
        isOpen={confirm?.kind === 'revoke'}
        title="담당자 해제"
        message={
          confirm?.kind === 'revoke'
            ? superAdminIds.has(confirm.row.userId)
              ? `${confirm.row.userName || confirm.row.userId}님을 명단에서 뺄까요? Super Admin이라 명단에서 빠져도 계속 담당자입니다.`
              : `${confirm.row.userName || confirm.row.userId}님을 원격지원 담당자에서 해제할까요? 진행 중인 원격지원이 있으면 즉시 끝납니다.`
            : ''
        }
        confirmLabel="해제"
        danger
        onConfirm={() => {
          if (confirm?.kind === 'revoke') performRevoke(confirm.row);
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        isOpen={confirm?.kind === 'disable'}
        title="원격지원 끄기"
        message="원격지원을 끌까요? 진행 중인 원격지원도 즉시 끝나고, 다시 켤 때까지 아무도 요청할 수 없습니다."
        confirmLabel="끄기"
        danger
        onConfirm={() => {
          applyPolicy({ enabled: false });
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        isOpen={confirm?.kind === 'disable-control'}
        title="원격 제어 막기"
        message="원격 제어를 막을까요? 제어 중인 세션은 즉시 화면 보기로 돌아갑니다."
        confirmLabel="막기"
        danger
        onConfirm={() => {
          applyPolicy({ controlEnabled: false });
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        isOpen={confirm?.kind === 'disable-files'}
        title="파일 주고받기 막기"
        message="원격지원 중 파일 주고받기를 막을까요? 주고받던 파일은 즉시 멈추고, 화면 보기·제어는 계속 쓸 수 있습니다."
        confirmLabel="막기"
        danger
        onConfirm={() => {
          applyPolicy({ fileTransferEnabled: false });
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

const PolicyRow: React.FC<{
  icon: React.ElementType;
  title: string;
  on: boolean;
  description: string;
  canToggle: boolean;
  busy: boolean;
  onToggle: () => void;
}> = ({ icon: Icon, title, on, description, canToggle, busy, onToggle }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <Icon size={16} className={on ? 'text-brand-600' : 'text-slate-400'} aria-hidden="true" />
        <span className="text-sm font-medium text-slate-800">{title}</span>
        <span
          className={`px-2 py-0.5 text-xs rounded-full font-medium ${
            on ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-600'
          }`}
        >
          {on ? '켜짐' : '꺼짐'}
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500">{description}</p>
    </div>
    {canToggle && (
      <button
        type="button"
        onClick={onToggle}
        disabled={busy}
        className={`shrink-0 px-3 py-1.5 text-sm rounded-lg transition-colors disabled:opacity-50 ${
          on
            ? 'bg-white border border-brand-600 text-brand-600 hover:bg-brand-50'
            : 'bg-brand-600 text-white hover:bg-brand-700'
        }`}
      >
        {on ? '끄기' : '켜기'}
      </button>
    )}
  </div>
);

const SummaryChip: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-full">
    <span className="text-slate-400">{label}</span>
    <span className="font-medium text-slate-700 tabular-nums">{value}</span>
  </span>
);
